'use client';

import { useState } from 'react';
import FieldRenderer from '@/app/components/molecules/FieldRenderer';
import Button from '@/app/components/atoms/Button';
import { getFieldType, normaliseField } from '@/lib/form-normalise';
import type { FormConfig, FormFieldConfig } from '@/app/components/types';

type FormValues = Record<string, string | boolean>;

interface DynamicFormProps {
  /** Form structure and field definitions. */
  config: FormConfig;
  /** Initial values keyed by field name. */
  initialValues?: FormValues;
  /** Callback fired on successful submit, receives the current form data. */
  onSubmit: (data: FormValues) => void;
  /** Whether the form is in a loading/submitting state. */
  loading?: boolean;
  /** Per-field error messages to display (e.g. from server validation). */
  fieldErrors?: Record<string, string>;
  /** Client-side validation: returns a map of field name → error message. */
  validate?: (data: FormValues) => Record<string, string>;
  /** Optional secondary action callback rendered as a second button (e.g. Cancel). */
  onSecondaryAction?: () => void;
  /** Optional content rendered after fields and before action buttons. */
  children?: React.ReactNode;
  /** Fired on every field change, receives the field name and new value. */
  onFieldChange?: (name: string, value: string | boolean) => void;
  /** External condition to disable the submit button. */
  disableSubmit?: boolean;
  /** When true, the submit button is not disabled when values match initialValues. */
  allowUnchangedSubmit?: boolean;
}

const PHONE_ALLOWED_CHARS_REGEX = /^[0-9()\-\s+.]+$/;

function validateTelField(field: FormFieldConfig, value: string | boolean): string | null {
  if (field.type !== 'tel') {
    return null;
  }

  const phone = String(value ?? '').trim();

  if (!phone) {
    return field.required ? 'Phone number is required' : null;
  }

  if (!PHONE_ALLOWED_CHARS_REGEX.test(phone)) {
    return 'Phone number contains invalid characters';
  }

  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) {
    return 'Phone number must contain exactly 10 digits';
  }

  return null;
}

function getBuiltInValidationErrors(
  fields: FormFieldConfig[],
  data: FormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};

  fields.forEach((field) => {
    const error = validateTelField(field, data[field.name] ?? '');
    if (error) {
      errors[field.name] = error;
    }
  });

  return errors;
}

/**
 * @component DynamicForm
 * @description Reusable data-driven form. Renders fields from a JSON config,
 * manages its own form state, runs optional client-side validation, and delegates
 * submit to the parent. Designed for use inside a FormCard across login, profile,
 * registration, and guest pass flows.
 * @param config - Form configuration including fields and submit label.
 * @param initialValues - Default values for each field.
 * @param onSubmit - Callback receiving form data on submit.
 * @param loading - Disables the form and shows a loading label while submitting.
 * @param fieldErrors - Server-side per-field error messages.
 * @param validate - Optional client-side validation function.
 * @returns The rendered form.
 */
export default function DynamicForm({
  config,
  initialValues = {},
  onSubmit,
  loading,
  fieldErrors: externalErrors,
  validate,
  onSecondaryAction,
  children,
  onFieldChange,
  disableSubmit,
  allowUnchangedSubmit,
}: DynamicFormProps) {
  const [formData, setFormData] = useState<FormValues>(initialValues);
  const [internalErrors, setInternalErrors] = useState<Record<string, string>>({});

  const fieldErrors = { ...internalErrors, ...externalErrors };

  const hasMissingRequiredValues = config.fields
    .filter((f) => f.required && !f.readOnly && !f.disabled)
    .some((f) => !String(formData[f.name] ?? '').trim());

  const hasUnchangedValues = config.fields.every((field) => {
    const currentValue = formData[field.name] ?? '';
    const initialValue = initialValues[field.name] ?? '';

    if (typeof currentValue === 'boolean' || typeof initialValue === 'boolean') {
      return Boolean(currentValue) === Boolean(initialValue);
    }

    return String(currentValue).trim() === String(initialValue).trim();
  });

  const isSubmitDisabled = loading || hasMissingRequiredValues || (!allowUnchangedSubmit && hasUnchangedValues) || !!disableSubmit;

  const handleChange = (name: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    onFieldChange?.(name, value);
    if (internalErrors[name]) {
      setInternalErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    // Normalise all text fields (title-case names, lowercase email, strip phone digits, etc.)
    const normalisedData = { ...formData };
    const normaliseErrors: Record<string, string> = {};
    config.fields.forEach((field) => {
      if (field.disabled || field.readOnly || field.type === 'checkbox' || field.type === 'password') return;
      const raw = String(formData[field.name] ?? '');
      const { value, error } = normaliseField(getFieldType(field.name), raw);
      normalisedData[field.name] = value;
      if (error) normaliseErrors[field.name] = error;
    });

    const customErrors = validate ? validate(normalisedData) : {};
    // Run built-in tel validation against raw input so invalid-character check fires before normalisation strips them
    const builtInErrors = getBuiltInValidationErrors(config.fields, formData);
    const errors = { ...builtInErrors, ...normaliseErrors, ...customErrors };

    if (Object.keys(errors).length > 0) {
      setInternalErrors(errors);
      return;
    }

    setFormData(normalisedData);
    setInternalErrors({});
    onSubmit(normalisedData);
  };

  return (
    <form
      id={config.id}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      aria-labelledby={config.ariaLabelledBy}
    >
      {config.fields.map((field) => (
        <FieldRenderer
          key={field.name}
          field={field}
          value={formData[field.name] ?? ''}
          onChange={handleChange}
          error={fieldErrors[field.name]}
          onBlurError={(name, err) => {
            setInternalErrors((prev) => {
              const next = { ...prev };
              if (err) next[name] = err;
              else delete next[name];
              return next;
            });
          }}
        />
      ))}

      {children}

      {!config.hideActions && (
        <div className="flex flex-col gap-3">
          <Button type="submit" variant="primary" size="full" disabled={isSubmitDisabled} loading={loading}>
            {config.submitLabel}
          </Button>
          {config.secondaryActionLabel && onSecondaryAction && (
            <Button type="button" variant="outline" size="full" onClick={onSecondaryAction}>
              {config.secondaryActionLabel}
            </Button>
          )}
        </div>
      )}
    </form>
  );
}
